import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import { fmtDate, money, todayIso } from "./common";

export const ENTRY_TYPE_LABELS = {
  FINANCIAL_CREDIT_NOTE: "Financial credit note",
  VOUCHER_CREDIT_NOTE: "Voucher credit note",
  ITEM_RETURN_CREDIT_NOTE: "Goods returned (not on a bill)",
  ADJUSTMENT: "Adjusted against bill",
};

/** A customer's account balance: credit notes raised against the customer and what was set off against bills. */
export const fetchCustomerAccount = (customerId) => api.get(`/api/v1/customers/${customerId}/account`);

/**
 * The customer's account: balance, every entry (financial / voucher / goods-return credit notes in, adjustments against bills
 * out) and a form to raise a financial or voucher credit note on the account.
 */
const CustomerAccountModal = ({ customerId, customerName, onClose, onChanged }) => {
  const [account, setAccount] = useState(null);
  const [type, setType] = useState("FINANCIAL_CREDIT_NOTE");
  const [amount, setAmount] = useState("");
  const [noteDate, setNoteDate] = useState(todayIso());
  const [referenceNumber, setReferenceNumber] = useState("");
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchCustomerAccount(customerId)
      .then(setAccount)
      .catch((error) => toast.error(error.message || "Could not load the customer's account"));
  }, [customerId]);

  /** Goods taken back while credit notes need approval: approving puts them in stock and the credit on the balance. */
  const approve = async (entry) => {
    try {
      const result = await api.post(`/api/v1/customers/${customerId}/account/entries/${entry.id}/approve`, {});
      setAccount(result);
      toast.success(`${entry.documentNumber || "Entry"} approved — stock updated and ₹${money(entry.amount)} added to the balance`);
      onChanged?.(result);
    } catch (error) {
      toast.error(error.message || "Could not approve");
    }
  };

  const create = async (e) => {
    e.preventDefault();
    if (!(Number(amount) > 0)) {
      toast.error("Enter the credit note amount");
      return;
    }
    setSaving(true);
    try {
      const result = await api.post(`/api/v1/customers/${customerId}/account/credit-notes`, {
        type,
        amount: Number(amount),
        noteDate: noteDate || null,
        referenceNumber: referenceNumber.trim() || null,
        remarks: remarks.trim() || null,
      });
      setAccount(result);
      setAmount("");
      setReferenceNumber("");
      setRemarks("");
      toast.success(`Credit note added; account balance ₹${money(result.balance)}`);
      onChanged?.(result);
    } catch (error) {
      console.error("Customer credit note failed", error);
      toast.error(error.message || "Could not create the credit note");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth: 920 }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold">Customer account — {account?.customerName || customerName}</h3>
          <button className="close-btn" onClick={onClose}>
            Close
          </button>
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Balance: ₹{money(account?.balance ?? 0)}</div>
        <div className="page-subtitle" style={{ marginBottom: 12 }}>
          Financial and voucher credit notes, and goods taken back that were not on a bill, are added here. Set the balance off
          against a bill from the payments page: Credit Note → "Adjust from account balance".
        </div>

        <form onSubmit={create} className="page-panel" style={{ padding: "12px 14px" }}>
          <strong>New credit note on the account</strong>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end", marginTop: 8 }}>
            <div className="form-group">
              <label>Type</label>
              <select value={type} onChange={(e) => setType(e.target.value)}>
                <option value="FINANCIAL_CREDIT_NOTE">Financial</option>
                <option value="VOUCHER_CREDIT_NOTE">Voucher</option>
              </select>
            </div>
            <div className="form-group" style={{ width: 130 }}>
              <label>Amount</label>
              <input type="number" min="0.01" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Date</label>
              <input type="date" value={noteDate} max={todayIso()} onChange={(e) => setNoteDate(e.target.value)} />
            </div>
            <div className="form-group" style={{ width: 150 }}>
              <label>Reference / voucher no</label>
              <input value={referenceNumber} maxLength={60} onChange={(e) => setReferenceNumber(e.target.value)} />
            </div>
            <div className="form-group" style={{ flex: 1, minWidth: 180 }}>
              <label>Remarks</label>
              <input value={remarks} maxLength={500} onChange={(e) => setRemarks(e.target.value)} />
            </div>
            <button type="submit" className="primary" disabled={saving}>
              {saving ? "Saving..." : "Add credit note"}
            </button>
          </div>
        </form>

        <div className="table-wrapper" style={{ maxHeight: "45vh", overflowY: "auto" }}>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Document</th>
                <th>Type</th>
                <th>Bill</th>
                <th>Reference</th>
                <th>Remarks</th>
                <th style={{ textAlign: "right" }}>Amount</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {!account ? (
                <tr>
                  <td colSpan={8} className="page-subtitle" style={{ textAlign: "center" }}>
                    Loading...
                  </td>
                </tr>
              ) : account.entries.length === 0 ? (
                <tr>
                  <td colSpan={8} className="page-subtitle" style={{ textAlign: "center" }}>
                    No entries yet
                  </td>
                </tr>
              ) : (
                account.entries.map((entry) => (
                  <tr key={entry.id}>
                    <td>{fmtDate(entry.entryDate)}</td>
                    <td>{entry.documentNumber || "-"}</td>
                    <td>
                      {ENTRY_TYPE_LABELS[entry.type] || entry.type}
                      {entry.items?.length ? (
                        <div className="page-subtitle" style={{ whiteSpace: "normal" }}>
                          {entry.items.map((item) => `${item.productName} × ${Number(item.quantity)}`).join(", ")}
                        </div>
                      ) : null}
                    </td>
                    <td>{entry.invoiceNumber || "-"}</td>
                    <td>{entry.referenceNumber || "-"}</td>
                    <td style={{ whiteSpace: "normal" }}>{entry.remarks || "-"}</td>
                    <td style={{ textAlign: "right", color: Number(entry.amount) < 0 ? "var(--color-danger)" : undefined }}>
                      {Number(entry.amount) < 0 ? "−" : "+"}₹{money(Math.abs(Number(entry.amount)))}
                    </td>
                    <td>
                      {entry.approvalPending ? (
                        <>
                          <span className="badge badge-amber" title="Not in stock and not on the balance until approved">
                            Pending approval
                          </span>{" "}
                          <button className="secondary" onClick={() => approve(entry)}>
                            Approve
                          </button>
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CustomerAccountModal;
